import React, { useEffect, useCallback, useState } from 'react'
import moment from 'moment-timezone'
import { parseQuery } from '@chilipiper/utils'
import { DotsLoader } from '@chilipiper/design-system'
import { useLocale } from '@chilipiper/locale'
import './times.styles.css'
import { ScreenReaderOnly, UnstyledButton } from '@chilipiper/design-system'

export interface Event {
  assigneeIds: string[]
  end: moment.Moment | number
  start: moment.Moment | number
}

interface Props {
  date: moment.Moment
  events: Event[]
  loading: boolean
  onChange: (time: moment.Moment, event: Event) => void
  time: moment.Moment | null
}

export const Times = ({ date, time: timeFromProps, loading, onChange, events }: Props) => {
  const locale = useLocale()
  const [eventsOfDay, setEventsOfDay] = useState<Event[]>([])
  const [time, setTime] = useState<moment.Moment | null>(null)
  const [debug, setDebug] = useState<boolean>(false)
  const timeHolder = 'fabe-time-holder'
  const query = parseQuery()

  const onSlotClick = useCallback(
    (start: moment.Moment, event: Event) => {
      setTime(start)
      onChange(start, event)
    },
    [onChange]
  )

  const timeFormatLocale = query.locale || locale

  const renderSlot = (event: Event) => {
    const item = moment(event.start)

    const selected = time?.isSame(item, 'minutes') ? 'fabe-slot-selected' : ''

    const timeSlotClassName = 'fabe-time-slot'

    const [hour, period] =
      timeFormatLocale === 'en_US'
        ? moment(item).locale(locale).format('LT').split(' ')
        : moment(item).locale(locale).format('HH:mm').split(' ')
    return (
      <UnstyledButton
        key={item.toISOString()}
        data-chili-css='time-slot-fill'
        className={`${timeSlotClassName} ${selected}`}
        onClick={() => {
          onSlotClick(item, event)
        }}
        data-test-id={`times:${hour}:${period ?? ''}`}
      >
        {debug && (
          <div className='fabe-debug-times-holder'>
            <span>{event.start.valueOf() || ''}</span>
            <span>{event.assigneeIds}</span>
          </div>
        )}
        <span>{hour}</span>
        {!!period && <span>{period}</span>}
      </UnstyledButton>
    )
  }

  useEffect(() => {
    setDebug(query.hiddendebug === 'true')
    const dayEvents = date
      ? events.filter(event => moment(event.start).isSame(date, 'day'))
      : events

    setEventsOfDay(dayEvents)
    setTime(timeFromProps)
  }, [loading, setEventsOfDay, events, setDebug, date, setTime, timeFromProps])

  if (loading) {
    return (
      <>
        <ScreenReaderOnly role='status'>Loading Time slots</ScreenReaderOnly>
        <DotsLoader color='neutral-dark-32' mt={4} height='100%' />
      </>
    )
  }

  return (
    <div className='fabe-time-chooser'>
      <div data-chili-css='slots-container' className={timeHolder}>
        {eventsOfDay.map(renderSlot)}
      </div>
    </div>
  )
}
